/**
 * Shared document → Word-compatible HTML rendering — the SINGLE per-type doc
 * serializer used by every surface (PK-QA-36). Pure, DOM-free string functions
 * (string/HTML builders only, zero browser/React dependency) so BOTH `@eer/web`
 * (the client-side `.doc` export) AND `@eer/api` (the forthcoming "final package"
 * link-generation endpoint that renders each artifact, uploads it to R2 and
 * returns a clickable URL) render each document through ONE implementation — no
 * second, divergable serializer of the same doc grammar.
 *
 * Relocated here from `packages/web/src/document/docExport.ts` (PK-QA-24 origin)
 * without behaviour change — the ONLY function that stayed behind is
 * `downloadArtifactDoc`, the DOM/browser side-effect wrapper (`Blob` /
 * `URL.createObjectURL` / `document.createElement` / anchor-click), which cannot
 * live in a DOM-free package. Everything else here is a pure string builder.
 * This mirrors PK-QA-35, which moved the pure markdown → HTML grammar to
 * `packages/shared/src/markdown.ts` for the exact same reason (single source of
 * truth for rendering across web + api).
 *
 * The `SOURCING_SHEET_FIELDS` spec `renderSourcingSheet` depends on moved here
 * too (it previously lived in the web-only `docViewModel.ts`); that module now
 * re-exports it from here so its existing web call sites are unchanged.
 *
 * No `dangerouslySetInnerHTML` in React: this HTML is only ever written into a
 * Blob the browser downloads (web) or uploaded as a `.doc` object (api), never
 * injected into the live DOM. Every content value is `esc`-escaped, so untrusted
 * LLM/user text can never inject raw HTML.
 */
import { GENERATION_LABELS } from './contracts/doc-generation.js';
import { INTERVIEW_QUESTION_FIELDS, SOURCING_SHEET_FIELDS, fieldValueHtml, isEmptyDocValue, resolveDocFields, valueHtml, } from './docFields.js';
import { esc, renderFreeMarkdown } from './markdown.js';
import { scrubStorageLinksDeep } from './storageLinks.js';
import { structureJdText } from './jdText.js';
import { dropEmptyListItems, isBlankListItem, looksLikeHtml, stripWrappingCodeFence, } from './richText.js';
/** Human label per doc type for the export heading + file name. */
const EXPORT_LABELS = {
    ...GENERATION_LABELS,
    sourcing_sheet: 'Sourcing Sheet',
    client_followup_email: 'Follow-up Email',
};
/** Resolve the display label for a doc type (fallback: the raw type). */
export function exportLabel(docType) {
    return EXPORT_LABELS[docType] ?? docType;
}
/** Render a `string[]` (or single string) as an HTML `<ul>` (empty → ''). */
function list(value) {
    const items = Array.isArray(value) ? value : value ? [value] : [];
    // KS3-06 — a bullet may itself be HTML (`<b>Go</b> — 5 years`); escaping it put
    // the tags in front of the reader.
    // E03-F08-T06 — a blank item (``, `  `, `<br>`) must not become an empty bullet.
    const rows = items
        .map(cellHtml)
        .filter((x) => !isBlankListItem(x))
        .map((x) => `<li>${x}</li>`)
        .join('');
    return rows ? `<ul>${rows}</ul>` : '';
}
/**
 * KS3-06 — value → HTML for a Word document.
 *
 * Ксения: «при download в ворде не отформатированный текст, а хтмл-теги». Причина
 * прямая: этот сериализатор ЭКРАНИРОВАЛ каждое значение, а после Этапа 4 значения
 * приходят от модели уже размеченными. `<b>Bold</b>` превращался в
 * `&lt;b&gt;Bold&lt;/b&gt;`, и Word честно показывал теги текстом.
 *
 * Решение то же, что во всех остальных местах после Этапа 4: решать ПО ЗНАЧЕНИЮ.
 * Готовый HTML проходит как разметка (через `normalizeRichText` — она же снимает
 * обрамляющее ```html-ограждение), всё остальное экранируется ровно как раньше,
 * поэтому старые markdown-артефакты в БД выглядят в Word без изменений.
 *
 * Экранирование остаётся ЕДИНСТВЕННОЙ защитой для не-HTML ветки: сюда приходит
 * текст модели, а результат уходит в файл, который откроет Word.
 */
function richValueHtml(value) {
    const text = stripWrappingCodeFence(value).trim();
    if (!text)
        return '';
    if (looksLikeHtml(text))
        return dropEmptyListItems(text);
    return text
        .split(/\n{2,}/)
        .map((p) => `<p>${esc(p).replace(/\n/g, '<br>')}</p>`)
        .join('');
}
/** Render a markdown-ish prose block: paragraphs split on blank lines. */
function prose(value) {
    return richValueHtml(value);
}
/** A one-line value for a table cell: HTML inline, plain text escaped (KS3-06). */
function cellHtml(value) {
    const text = stripWrappingCodeFence(value).trim();
    if (!text)
        return '';
    return looksLikeHtml(text) ? text : esc(text);
}
// `esc`, `inlineMd`, `renderFreeMarkdown` live in `./markdown.js` (PK-QA-35). The
// block renderer (`renderFreeMarkdown`) drives the `.doc` export for free-markdown
// types (kickoff_sheet body, sourcing-sheet markdown fallback) below via that
// import — behaviour is byte-identical.
/** A titled section (heading + body) — omitted when the body is empty. */
function section(title, bodyHtml) {
    if (!bodyHtml)
        return '';
    return `<h2>${esc(title)}</h2>${bodyHtml}`;
}
/**
 * JD (jd_doc) — заголовок должности + 5 разделов.
 *
 * `position_title_block` (главное название + альтернативные) до E12-F05-T02 не
 * попадал в экспорт вообще, хотя есть у всех восьми живых артефактов и виден на
 * экране: документ уезжал клиенту без названия вакансии.
 * `*_comments` — внутренние комментарии рекрутёра, им в JD не место.
 */
function renderJd(c) {
    const titleBlock = (c['position_title_block'] ?? {});
    const mainTitle = stripWrappingCodeFence(titleBlock['main_title']).trim();
    const alternatives = Array.isArray(titleBlock['alternative_titles'])
        ? titleBlock['alternative_titles'].map((t) => String(t)).filter(Boolean)
        : [];
    const head = (mainTitle ? `<h2>${esc(mainTitle)}</h2>` : '') +
        (alternatives.length > 0
            ? `<p><em>Alternative titles: ${esc(alternatives.join(' | '))}</em></p>`
            : '');
    return [
        head,
        section('About Company', prose(c['about_company'])),
        section('About Role', prose(c['about_role'])),
        section('Responsibilities', list(c['responsibilities'])),
        section('Requirements', list(c['requirements'])),
        section('What Company Offers', prose(c['company_offers'])),
    ].join('');
}
/**
 * Interview Questions — по заголовку на блок вопросов.
 *
 * Блоки берутся из общей спецификации со псевдонимами, а незнакомые непустые —
 * следом (E12-F05-T02). До этого список имён был зашит здесь, и артефакты,
 * которые промпт вернул под новыми именами, рендерились в ПУСТОЙ документ:
 * ни одного вопроса, хотя в базе их полсотни. Пустые блоки пропускаются —
 * правило KS3-07 «показывать пустое» относится к бланку Sourcing Sheet, а не к
 * списку вопросов.
 */
function renderInterviewQuestions(c) {
    const named = resolveDocFields(INTERVIEW_QUESTION_FIELDS, c, ['questions'])
        .filter((f) => !f.empty)
        .map((f) => section(f.label, valueHtml(f.value, { ordered: true })))
        .join('');
    // Flat fallback list some variants return.
    return named || list(c['questions']);
}
/** InMails — one card per variant (tone / subject / body). */
function renderInmails(c) {
    const variants = Array.isArray(c['variants']) ? c['variants'] : [];
    if (variants.length === 0)
        return '';
    return variants
        .map((v, i) => {
        const o = (v ?? {});
        const tone = o['tone'] ?? o['label'] ?? `Variant ${i + 1}`;
        const subject = o['subject'] ? `<p><strong>Subject:</strong> ${esc(o['subject'])}</p>` : '';
        const body = prose(typeof v === 'string' ? v : o['body']);
        return `<h3>${esc(tone)}</h3>${subject}${body}`;
    })
        .join('');
}
/**
 * Kickoff / Sourcing sheet — FREE-markdown body (kickoff_sheet | text). PK-QA-24:
 * parse markdown (headings / bullets / inline emphasis + links) instead of dumping
 * the literal `**`/`###`/`- ` into the `.doc`.
 */
function renderMarkdownDoc(c) {
    const body = c['kickoff_sheet'] ?? c['text'] ?? '';
    // E12-F05-T02 — тот же выбор ПО ЗНАЧЕНИЮ, что и в `richValueHtml` (ADR KS3-04/
    // KS3-06). Этот путь его не делал: `renderFreeMarkdown` экранирует всё подряд,
    // поэтому Kickoff Sheet, который модель после Этапа 4 отдала HTML-ом (живой
    // артефакт #292, проект 168), скачивался в Word как `<h1>KICKOFF SHEET</h1>`
    // текстом. На экране он выглядел правильно — там решение по значению уже было,
    // и расхождение экрана с файлом никто не видел.
    const text = stripWrappingCodeFence(body).trim();
    if (!text)
        return '';
    // E03-F08-T06 — model HTML may carry `<li></li>` / `<li>&nbsp;</li>`: drop them.
    return looksLikeHtml(text) ? dropEmptyListItems(text) : renderFreeMarkdown(body);
}
/**
 * Sourcing Sheet — structured serializer: a label/value table of the ~24 fields
 * (arrays joined with ", ", `brief_summary` as a full-width prose block). Falls
 * back to the plain-markdown body when no structured field is present (older AI
 * output stored the whole sheet as a single `text`/`kickoff_sheet` markdown blob).
 */
function renderSourcingSheet(c) {
    // `text` / `kickoff_sheet` — это тело листа прозой, у него своё место (ветка
    // ниже), поэтому страховка не должна вынести его ещё и отдельной строкой.
    const fields = resolveDocFields(SOURCING_SHEET_FIELDS, c, ['text', 'kickoff_sheet']);
    if (fields.every((f) => !f.known || f.empty))
        return renderMarkdownDoc(c);
    // KS3-07 — EVERY field of the spec is rendered, filled or not. An unfilled row
    // keeps its label and an empty cell; an unfilled block keeps its heading and a
    // blank paragraph under it. Dropping them made the exported sheet look like a
    // document that never had those sections, so nothing pointed at what to fill in.
    //
    // E12-F05-T02 — значение рисуется по своей форме (`valueHtml`): строка, список,
    // вложенный объект с приоритетами. Раньше в ячейку уходил `String(value)`, и
    // `{priority_1_must: [...]}` показывался бы как `[object Object]` — поэтому
    // такие поля просто не были включены в спецификацию и терялись целиком.
    const rows = [];
    const blocks = [];
    for (const f of fields) {
        if (f.block) {
            blocks.push(`<h3>${esc(f.label)}</h3>${f.empty ? '<p>&nbsp;</p>' : prose(f.value)}`);
            continue;
        }
        const cell = f.empty ? '&nbsp;' : fieldValueHtml(f.value);
        rows.push(`<tr><td><strong>${esc(f.label)}</strong></td><td>${cell}</td></tr>`);
    }
    const table = rows.length > 0 ? `<table>${rows.join('')}</table>` : '';
    return `${table}${blocks.join('')}`;
}
/**
 * Kickoff Sheet — сам лист плюс документы, которые лежат в той же строке
 * артефакта: `sourcing_sheet` и `jd_copy`.
 *
 * E12-F05-T02: на экране карточка кикофа показывает их обоих (`appendNested` в
 * `docViewModel`), а в скачанный файл и во вложение письма попадал только сам
 * лист — то есть человек отправлял клиенту документ короче того, что видел. Из
 * восьми живых артефактов вложенные документы есть у трёх.
 */
function renderKickoffSheet(raw) {
    // E03-F08-T07 — a storage address / signed link is never document content
    // (sheets written before the rule carried `name: https://…r2…` lines).
    const c = scrubStorageLinksDeep(raw);
    const parts = [renderMarkdownDoc(c)];
    // E03-F08-T07 — order kick-off → Sourcing Sheet → Original JD, and BOTH blocks
    // are always present (KS3-07 rule: an unfilled block keeps its heading).
    const sourcing = unwrapNestedSourcing(c['sourcing_sheet']);
    parts.push(`<hr><h2>${KICKOFF_SOURCING_HEADING}</h2>`);
    if (isEmptyDocValue(sourcing)) {
        parts.push('<p>&nbsp;</p>');
    }
    else {
        parts.push(typeof sourcing === 'string'
            ? renderMarkdownDoc({ text: sourcing })
            : renderSourcingSheet(sourcing));
    }
    const jd = c['jd_copy'];
    parts.push(`<hr><h2>${KICKOFF_JD_HEADING}</h2>`);
    if (isEmptyDocValue(jd)) {
        parts.push('<p>&nbsp;</p>');
    }
    else {
        // E03-F08-T08 — paragraphs/bullets of the client's JD (also repairs stored
        // sheets whose JD text was extracted as one run-on line).
        parts.push(typeof jd === 'string'
            ? renderMarkdownDoc({ text: structureJdText(jd) })
            : renderJd(jd));
    }
    return parts.join('');
}
/** E03-F08-T07 — the two appendix headings of the Kickoff Sheet (view + export). */
export const KICKOFF_SOURCING_HEADING = 'Sourcing Sheet';
export const KICKOFF_JD_HEADING = 'Original JD from client';
/**
 * Модель иногда кладёт в слот `sourcing_sheet` объект, внутри которого лежит ещё
 * один `sourcing_sheet` (legacy L465). Тот же разворот делает просмотр.
 */
function unwrapNestedSourcing(value) {
    if (value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        'sourcing_sheet' in value &&
        !('position_title' in value)) {
        return value['sourcing_sheet'];
    }
    return value;
}
/** Follow-up / internal email — subject + body. */
function renderEmail(c) {
    const subject = c['subject'] ? `<p><strong>Subject:</strong> ${esc(c['subject'])}</p>` : '';
    return `${subject}${richValueHtml(c['body'])}`;
}
/**
 * Render an artifact's `content` into the structured HTML body for its type
 * (legacy `renderDocContentHtml`). Unknown types fall back to a JSON dump so a
 * download never produces an empty file.
 */
export function renderDocContentHtml(docType, content) {
    const c = (content && typeof content === 'object' ? content : {});
    switch (docType) {
        case 'jd_doc':
            return renderJd(c);
        case 'interview_questions':
            return renderInterviewQuestions(c);
        case 'inmails':
            return renderInmails(c);
        case 'kickoff_sheet':
            return renderKickoffSheet(c);
        case 'sourcing_sheet':
            return renderSourcingSheet(c);
        case 'client_followup_email':
        case 'internal_notification':
            return renderEmail(c);
        default:
            return `<pre>${esc(JSON.stringify(content, null, 2))}</pre>`;
    }
}
/** Build the Word-compatible HTML document shell (legacy blob wrapper). */
export function buildDocHtml(params) {
    const { label, clientName, roleName, bodyHtml } = params;
    return `
      <html xmlns:o="urn:schemas-microsoft-com:office:office"
            xmlns:w="urn:schemas-microsoft-com:office:word"
            xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8">
        <!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View></w:WordDocument></xml><![endif]-->
        <style>
          body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; line-height: 1.5; color: #333; margin: 2cm; }
          h1 { font-size: 16pt; color: #0f172a; margin-bottom: 8pt; }
          h2 { font-size: 14pt; color: #0f172a; margin-top: 16pt; margin-bottom: 6pt; }
          h3 { font-size: 12pt; color: #334155; margin-top: 12pt; margin-bottom: 4pt; }
          ul, ol { margin-left: 20pt; }
          li { margin-bottom: 4pt; }
          table { border-collapse: collapse; width: 100%; margin: 8pt 0; }
          td, th { border: 1px solid #ddd; padding: 6pt 8pt; text-align: left; font-size: 10pt; }
          th { background-color: #f1f5f9; font-weight: bold; }
          p { margin: 4pt 0; }
        </style>
      </head>
      <body>
        <h1>${esc(label)}</h1>
        <p style="color:#666; font-size:9pt;">Client: ${esc(clientName)} | Position: ${esc(roleName || '—')}</p>
        <hr style="border:none; border-top:1px solid #e2e8f0; margin:12pt 0;">
        ${bodyHtml}
      </body></html>`;
}
/** Compose the `.doc` file name (legacy: JD omits the client, others include it). */
export function docFileName(docType, clientName, roleName) {
    const label = exportLabel(docType);
    const role = roleName ? ` - ${roleName}` : '';
    return docType === 'jd_doc' ? `${label}${role}.doc` : `${label} - ${clientName}${role}.doc`;
}
//# sourceMappingURL=docRender.js.map